import { cn } from "@core-ui/utils/cn";
import t from "@ext/localization/locale/translate";
import { IconButton } from "@ui-kit/Button";
import { ConfirmMenuItem } from "@ui-kit/ConfirmPopover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@ui-kit/Dropdown";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui-kit/Tooltip";
import { forwardRef, type InputHTMLAttributes, useRef, useState } from "react";
import { convertSectionToFolderConfirmation, homepageDeleteConfirmation } from "../confirmations";
import { normalizeTitle } from "../utils/normalizeTitle";
import type { SectionEditActions } from "./sectionTypes";

const TITLE_CLASS_NAME = "font-semibold py-[3px] tracking-[-0.1px] text-primary-fg";

interface SectionTitleInputProps extends InputHTMLAttributes<HTMLInputElement> {
	value: string;
	onTitleChange: (title: string) => void;
}

const SectionTitleInput = forwardRef<HTMLInputElement, SectionTitleInputProps>(
	({ value, onTitleChange, className, ...props }, ref) => {
		const [draft, setDraft] = useState<string | null>(null);
		const cancelled = useRef(false);
		const displayed = draft ?? value;

		const handleBlur = () => {
			const next = draft === null ? "" : normalizeTitle(draft);
			if (!cancelled.current && next && next !== value) onTitleChange(next);
			cancelled.current = false;
			setDraft(null);
		};

		// `field-sizing: content` would size the input natively, but older system WebViews (Tauri) do not support it yet.
		return (
			<span
				className={cn(
					"inline-grid min-w-0 max-w-full grid-cols-[minmax(0,auto)] [&>*]:[grid-area:1/1]",
					TITLE_CLASS_NAME,
					className,
				)}
			>
				<span aria-hidden className="invisible overflow-hidden whitespace-pre">
					{`${displayed} `}
				</span>
				<input
					aria-label={t("section-title")}
					{...props}
					className="w-0 min-w-full border-0 bg-transparent p-0 text-inherit outline-none [font:inherit] [letter-spacing:inherit]"
					onBlur={handleBlur}
					onChange={(e) => setDraft(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Escape") {
							cancelled.current = true;
							e.currentTarget.blur();
						} else if (e.key === "Enter" && !e.nativeEvent.isComposing) {
							e.currentTarget.blur();
						}
					}}
					ref={ref}
					value={displayed}
				/>
			</span>
		);
	},
);

interface GroupHeaderProps {
	title?: string;
	editActions?: SectionEditActions;
}

/** `editActions` is the whole permission model here: present means the section is editable, absent means it is not. */
const GroupHeader = ({ title, editActions }: GroupHeaderProps) => {
	const titleInputRef = useRef<HTMLInputElement>(null);
	const renameRequested = useRef(false);

	if (!editActions) {
		return (
			<div className="flex items-center gap-2">
				<span className={TITLE_CLASS_NAME}>{title}</span>
			</div>
		);
	}

	return (
		<div className="flex items-center gap-2 group/hd">
			<SectionTitleInput onTitleChange={editActions.onTitleChange} ref={titleInputRef} value={title ?? ""} />
			<span className="flex-1" />
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<IconButton
						aria-label={t("more-actions")}
						className="opacity-0 transition-opacity duration-[140ms] group-hover/section:opacity-100 group-focus-within/hd:opacity-100 data-[state=open]:opacity-100"
						icon="ellipsis"
						onClick={(e) => e.stopPropagation()}
						onPointerDown={(e) => e.stopPropagation()}
						size="sm"
						variant="ghost"
					/>
				</DropdownMenuTrigger>
				<DropdownMenuContent
					align="end"
					onCloseAutoFocus={(e) => {
						e.preventDefault();
						if (!renameRequested.current) return;
						renameRequested.current = false;
						titleInputRef.current?.focus();
						titleInputRef.current?.select();
					}}
				>
					<DropdownMenuItem
						onClick={(e) => {
							e.stopPropagation();
							renameRequested.current = true;
						}}
					>
						{t("rename")}
					</DropdownMenuItem>
					{editActions.convertToFolderDisabled ? (
						<Tooltip>
							<TooltipTrigger className="cursor-default">
								<DropdownMenuItem disabled>{t("convert-to-folder")}</DropdownMenuItem>
							</TooltipTrigger>
							<TooltipContent>{t("convert-to-folder-disabled-empty")}</TooltipContent>
						</Tooltip>
					) : (
						<ConfirmMenuItem
							confirm={
								editActions.convertToFolderNeedsConfirm
									? convertSectionToFolderConfirmation()
									: undefined
							}
							onClick={(e) => e.stopPropagation()}
							onConfirm={editActions.onConvertToFolder}
						>
							{t("convert-to-folder")}
						</ConfirmMenuItem>
					)}
					<ConfirmMenuItem
						confirm={homepageDeleteConfirmation("section")}
						onClick={(e) => e.stopPropagation()}
						onConfirm={editActions.onDelete}
						type="danger"
					>
						{t("delete")}
					</ConfirmMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
};

export default GroupHeader;
